import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuthStore, useIsAdmin, useAffiliatedSiteIds, useIsUserManager } from '../../lib/auth';

export function TopBar() {
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);
  const isAdmin = useIsAdmin();
  const isUserManager = useIsUserManager();
  const siteIds = useAffiliatedSiteIds();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Desktop Nav */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.svg" alt="SmartField Logo" className="w-8 h-8" />
              <span className="text-2xl font-bold text-smartfield">SmartField</span>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Link 
                to="/" 
                className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
                  isActive('/') && location.pathname === '/'
                    ? 'text-smartfield border-smartfield' 
                    : 'text-gray-700 hover:text-smartfield border-transparent hover:border-gray-300'
                }`}
              >
                Dashboard
              </Link>
              <Link 
                to="/sites" 
                className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
                  isActive('/sites')
                    ? 'text-smartfield border-smartfield' 
                    : 'text-gray-700 hover:text-smartfield border-transparent hover:border-gray-300'
                }`}
              >
                Sites
              </Link>
              <Link
                to="/data/export"
                className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
                  isActive('/data')
                    ? 'text-smartfield border-smartfield' 
                    : 'text-gray-700 hover:text-smartfield border-transparent hover:border-gray-300'
                }`}
              >
                Data
              </Link>
              {(isAdmin || isUserManager) && (
                <Link 
                  to="/account/users" 
                  className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
                    isActive('/account')
                      ? 'text-smartfield border-smartfield' 
                      : 'text-gray-700 hover:text-smartfield border-transparent hover:border-gray-300'
                  }`}
                >
                  Users
                </Link>
              )}
            </div>
          </div>
          
          {/* Desktop User Info */}
          <div className="hidden md:flex items-center gap-3">
            <div className="text-sm text-gray-700 font-medium">
              {user?.email}
            </div>
            <Link
              to="/account/manage"
              className="p-2 text-gray-600 hover:text-smartfield hover:bg-gray-50 rounded-md transition-colors"
              aria-label="Account Settings"
              title="Account Settings"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </Link>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-600 hover:text-smartfield hover:bg-gray-50 rounded-md transition-colors"
              aria-label="Logout"
              title="Logout"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-smartfield p-2 rounded-md transition-colors"
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col gap-2">
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive('/') && location.pathname === '/'
                    ? 'text-smartfield bg-smartfield-50' 
                    : 'text-gray-700 hover:text-smartfield hover:bg-gray-50'
                }`}
              >
                Dashboard
              </Link>
              <Link 
                to="/sites" 
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive('/sites')
                    ? 'text-smartfield bg-smartfield-50' 
                    : 'text-gray-700 hover:text-smartfield hover:bg-gray-50'
                }`}
              >
                Sites
              </Link>
              <Link
                to="/data/export"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive('/data')
                    ? 'text-smartfield bg-smartfield-50' 
                    : 'text-gray-700 hover:text-smartfield hover:bg-gray-50'
                }`}
              >
                Data
              </Link>
              {(isAdmin || isUserManager) && (
                <Link 
                  to="/account/users" 
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                    isActive('/account')
                      ? 'text-smartfield bg-smartfield-50' 
                      : 'text-gray-700 hover:text-smartfield hover:bg-gray-50'
                  }`}
                >
                  Users
                </Link>
              )}
              <div className="border-t border-gray-200 mt-2 pt-2">
                <div className="px-3 py-2 text-sm text-gray-600">
                  <div className="font-medium">{user?.email}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {isAdmin ? 'Admin' : `${siteIds.length} site${siteIds.length !== 1 ? 's' : ''}`}
                  </div>
                </div>
                <Link
                  to="/account/manage"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full text-left text-gray-700 hover:text-smartfield hover:bg-gray-50 px-3 py-2 text-sm font-medium transition-colors rounded-md flex items-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Account Settings
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full text-left text-gray-700 hover:text-smartfield hover:bg-gray-50 px-3 py-2 text-sm font-medium transition-colors rounded-md"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
